import React, { useEffect, useState } from "react";

import { gridSpacing } from "@utils";

import Select from "../Select/Select";
import type { SelectStyleProps } from "../Select/types";
import { getSelectStyleProps } from "../Select/types";

type AA = "AM" | "PM" | "";

const getHourOptions = (aa: AA | undefined, noStartHr: boolean) => {
  const options = [];
  let maxHr = 12;
  if (noStartHr && aa === "AM") {
    maxHr--;
  }

  for (let i = 1; i < maxHr + 1; i++) {
    let value;
    let label = "";
    if (i < 10) {
      value = `0${i}`;
      label = `0${i}`;
    } else if (i === 12 && aa === "AM") {
      value = "00";
      label = "12";
    } else {
      label = `${i}`;
      value = `${i}`;
    }
    options.push({ label, value });
  }

  return options;
};

const getMinOptions = () => {
  const options = [];
  for (let i = 0; i < 60; i++) {
    let value;
    if (i < 10) {
      value = `0${i}`;
    } else {
      value = `${i}`;
    }
    options.push({ label: value, value });
  }

  return options;
};

interface TimePickerProps extends SelectStyleProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  minOptions?: { label: string; value: string }[];
  use24Hr?: boolean;
  noStartHr?: boolean;
  disabled?: boolean;
  labelStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  minStyle?: React.CSSProperties;
  hrStyle?: React.CSSProperties;
  aaStyle?: React.CSSProperties;
  testId?: string;
}

// Note: This currently only supports 24hr, once a case is needed for 12 it should be changed
function TimePicker(props: TimePickerProps) {
  const {
    label,
    value,
    onChange,
    minOptions,
    use24Hr,
    noStartHr,
    disabled,
    minStyle,
    hrStyle,
    aaStyle,
    labelStyle,
    containerStyle,
  } = {
    use24Hr: true,
    noStartHr: false,
    disabled: false,
    minStyle: {},
    hrStyle: {},
    aaStyle: {},
    labelStyle: {},
    containerStyle: {},
    ...props,
  };

  const [time, setTime] = useState<{
    hr?: string;
    min?: string;
    aa: AA;
  }>({
    hr: "",
    min: "",
    aa: "",
  });

  useEffect(() => {
    if (value) {
      const splitTime = value.split(/[ :]+/);
      if (use24Hr) {
        let hr = splitTime[0];
        const min = splitTime[1];
        let aa: AA = "AM";

        if (hr === "00" && noStartHr) {
          hr = "11";
        } else if (parseInt(hr) > 11) {
          if (parseInt(hr) - 12 < 10) {
            hr = `0${parseInt(hr) - 12}`;
          } else {
            hr = `${parseInt(hr) - 12}`;
          }

          if (hr === "00") {
            hr = "12";
          }

          aa = "PM";
        }
        setTime({ hr, min, aa });
      } else {
        if (splitTime.length === 2) {
          setTime({ aa: splitTime[1] as AA });
        } else {
          setTime({
            hr: splitTime[0],
            min: splitTime[1],
            aa: splitTime[2] as AA,
          });
        }
      }
    } else {
      setTime({ hr: "", min: "", aa: "" });
    }
  }, [value]);

  const updateTime = (key: "aa" | "min" | "hr", value: string) => {
    const _time = { ...time };
    if (key === "aa") {
      _time.aa = value as AA;
    } else {
      _time[key] = value;
    }

    if (!_time.hr) {
      _time.hr = "09";
    }

    if (!_time.min) {
      _time.min = "00";
    }

    if (use24Hr) {
      let hr = _time.hr;
      if (_time.aa === "PM" && parseInt(_time.hr) !== 12) {
        hr = `${parseInt(_time.hr) + 12}`;
      } else if (_time.aa === "AM" && parseInt(_time.hr) === 12) {
        hr = `11`;
      }

      onChange(`${hr}:${_time.min}:00`);
    } else {
      onChange(`${_time.hr}:${_time.min} ${_time.aa}`);
    }
  };

  const content = (
    <>
      <Select
        {...getSelectStyleProps(props)}
        placeholder="Hr"
        style={{ width: 115, marginRight: gridSpacing[1], ...hrStyle }}
        options={getHourOptions(time.aa, noStartHr)}
        value={time.hr}
        onChange={(e) => updateTime("hr", e)}
        isDisabled={disabled}
      />
      <b>:</b>
      <Select
        {...getSelectStyleProps(props)}
        placeholder="Min"
        style={{
          width: 115,
          marginLeft: gridSpacing[1],
          marginRight: gridSpacing[2],
          ...minStyle,
        }}
        options={minOptions || getMinOptions()}
        value={time.min}
        onChange={(e) => updateTime("min", e)}
        isDisabled={disabled}
      />
      <Select
        {...getSelectStyleProps(props)}
        hideSearch
        placeholder="AM/PM"
        style={{ width: 115, marginRight: gridSpacing[2], ...aaStyle }}
        options={[
          { label: "AM", value: "AM" },
          { label: "PM", value: "PM" },
        ]}
        value={time.aa}
        onChange={(e) => updateTime("aa", e)}
        isDisabled={disabled}
      />
      {/* <Select
        placeholder="Choose Timezone"
        style={{ width: 325 }}
        isDisabled={true}
        options={[
          { label: "Atlantic (UTC-04:00)", value: "AST" },
          { label: "Eastern (UTC-05:00)", value: "EST" },
          { label: "Central (UTC-06:00)", value: "CST" },
          { label: "Mountain (UTC-07:00)", value: "MST" },
          { label: "Pacific (UTC-08:00)", value: "PST" },
          { label: "Alaska (UTC-09:00)", value: "AKST" },
          { label: "Hawaii (UTC-10:00)", value: "HST" },
        ]}
        value={data.schedule_timezone}
        onChange={(e) =>
          setData({
            ...data,
            schedule_timezone: e,
          })
        }
      /> */}
    </>
  );

  if (label) {
    return (
      <div>
        <p style={labelStyle}>{label}</p>
        <div style={{ display: "flex", ...containerStyle }}>{content}</div>
      </div>
    );
  }

  return content;
}

export default TimePicker;
